import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ChatMessage, FlaggedData } from 'src/interfaces/Chat';
import useResponsive from 'src/hooks/useResponsive';
import useViewportHeight from 'src/hooks/useViewPortHeight';
import ReadOnlyBotMessage from './BotMessage/ReadOnlyBotMessage';
import UserMessage from './UserMessage';
import ScrollToBottomButton from './Layout/ScrollToBottom';
import FeedbackModal from './Modals/FeedbackModal';
import { Tabs, TabsValue } from '@mantine/core';

interface ReadOnlyChatProps {
  messages: ChatMessage[];
  childUsername: string;
  onFlagMessage: (flaggedData: FlaggedData) => void;
  subjects?: string[];
  activeSubject?: string | null;
  onSubjectChange?: (subject: string) => void;
}

const ReadOnlyChat: React.FC<ReadOnlyChatProps> = (props) => {
  const {
    messages,
    childUsername,
    onFlagMessage,
    subjects = [],
    activeSubject = null,
    onSubjectChange = () => {},
  } = props;
  const viewport = useRef<HTMLDivElement>(null);
  const [isAtBottomButtonTrigger, setAtBottomButtonTrigger] = useState(true);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const { isMobile } = useResponsive();
  const [feedbackType, setFeedbackType] = useState<string | null>(null);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [flaggedMessageIndex, setFlaggedMessageIndex] = useState<number | null>(
    null
  );

  // useViewportHeight();

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const handleScrollButtonTrigger = () => {
      if (!viewport.current) return;
      const bottomThreshold = isMobile ? 120 : 100;
      const isBottom =
        viewport.current.scrollHeight - viewport.current.scrollTop <=
        viewport.current.clientHeight + bottomThreshold;
      setAtBottomButtonTrigger(isBottom);
    };

    const handleScroll = () => {
      if (!viewport.current) return;
      const bottomThreshold = isMobile ? 30 : 50;
      const isBottom =
        viewport.current.scrollHeight - viewport.current.scrollTop <=
        viewport.current.clientHeight + bottomThreshold;
      setIsAtBottom(isBottom);
    };

    viewport.current?.addEventListener('scroll', handleScrollButtonTrigger);
    viewport.current?.addEventListener('scroll', handleScroll);

    return () => {
      viewport.current?.removeEventListener(
        'scroll',
        handleScrollButtonTrigger
      );
      viewport.current?.removeEventListener('scroll', handleScroll);
    };
  }, [isMobile]);

  const scrollToBottom = () => {
    viewport.current?.scrollTo({
      top: viewport.current.scrollHeight,
      behavior: 'smooth',
    });
  };

  const handleFlagMessage = useCallback((index: number) => {
    setFlaggedMessageIndex(index);
    setShowFeedbackForm(true);
  }, []);

  const handleSubmitFeedback = useCallback(
    (feedback: string) => {
      if (flaggedMessageIndex === null) return;

      const startIndex = Math.max(1, flaggedMessageIndex - 10);
      const msgsToFlag = messages
        .slice(startIndex, flaggedMessageIndex + 1)
        .map((msg, i) => ({
          role: msg.role,
          content: msg.content,
          created_at: String(msg.created_at),
          is_flagged: i === flaggedMessageIndex - startIndex,
        }));

      const flaggedData: FlaggedData = {
        child_username: childUsername,
        msgs_to_flag: msgsToFlag,
        feedback: feedback,
      };
      onFlagMessage(flaggedData);
      handleCancelFeedback();
    },
    [flaggedMessageIndex, messages, childUsername, onFlagMessage, feedbackType]
  );

  const handleCancelFeedback = () => {
    setShowFeedbackForm(false);
    setFeedbackType(null);
    setFlaggedMessageIndex(null);
  };

  const handleCloseFeedbackModal = () => {
    setShowFeedbackForm(false);
    setFeedbackType(null);
    setFlaggedMessageIndex(null);
  };

  const handleSubjectChange = (value: TabsValue) => {
    if (value) {
      onSubjectChange(value);
    }
  };

  const tabList = useMemo(() => {
    return subjects.map((subject) => (
      <Tabs.Tab
        key={subject}
        value={subject}
        className='font-medium text-sm sm:text-base'
      >
        {subject}
      </Tabs.Tab>
    ));
  }, [subjects]);

  return (
    <div className='relative h-full w-full flex flex-col'>
      {/* Subject Tabs */}
      {subjects.length > 1 && (
        <div className='-mt-2'>
          <Tabs
            color='primary'
            value={activeSubject}
            onTabChange={handleSubjectChange}
            variant='pills'
            classNames={{
              tab: 'py-3 px-4 rounded-t-none rounded-b-xl bg-primary/20 [&[data-active]]:bg-primary [&[data-active]]:text-white hover:bg-primary hover:text-white hover:[&[data-active]]:bg-primary hover:[&[data-active]]:text-white',
              tabLabel: 'flex items-center gap-2',
              tabsList: 'w-full px-4 flex justify-center items-center pb-[1px]',
            }}
          >
            <Tabs.List position='left'>{tabList}</Tabs.List>
          </Tabs>
        </div>
      )}
      <div className='flex-1 overflow-y-auto py-5' ref={viewport}>
        {messages.map((message, index) => (
          <div key={index} className='mb-4'>
            {message.content.includes(
              "Hi! Your child hasn't sent any messages yet."
            ) ? (
              <ReadOnlyBotMessage
                message={message.content}
                index={index}
                alignLeft={true}
                onFlagMessage={handleFlagMessage}
              />
            ) : message.role === 'human' ? (
              <UserMessage message={message.content} />
            ) : (
              <ReadOnlyBotMessage
                message={message.content}
                index={index}
                onFlagMessage={handleFlagMessage}
              />
            )}
          </div>
        ))}
      </div>
      {!isAtBottomButtonTrigger && (
        <ScrollToBottomButton onClick={scrollToBottom} />
      )}
      <FeedbackModal
        isOpen={showFeedbackForm}
        onClose={handleCloseFeedbackModal}
        feedbackType={feedbackType}
        setFeedbackType={setFeedbackType}
        onSubmit={handleSubmitFeedback}
      />
    </div>
  );
};

export default ReadOnlyChat;
